/**
 * Управление темой кабинета (dark / light).
 *
 * Стратегия Tailwind: `darkMode: 'class'` — переключаем класс `dark`
 * на корневом <html>. По умолчанию тема ТЁМНАЯ; пользователь может
 * переключить через toggle в Header (AppShell). Выбор сохраняется
 * в localStorage и переживает перезагрузку.
 *
 * applyThemeBeforeReact() вызывается из main.tsx ДО рендера React —
 * иначе будет вспышка светлой темы (FOUC) перед монтированием.
 */

import { useCallback, useEffect, useState } from 'react';

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'drafter:cabinet:theme';
const DEFAULT_THEME: Theme = 'dark';

function read(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'dark' || v === 'light') return v;
  } catch {
    // localStorage недоступен (приватный режим / iframe-блок) — fallback.
  }
  return DEFAULT_THEME;
}

function apply(theme: Theme): void {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  // Telegram WebApp и iOS Safari читают theme-color для подстройки
  // системного UI (status bar). Меняем синхронно с переключением.
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) {
    meta.content = theme === 'dark' ? '#04061A' : '#ffffff';
  }
}

/** Вызывается из main.tsx до React. Предотвращает вспышку светлой темы. */
export function applyThemeBeforeReact(): void {
  apply(read());
}

/** React-хук: текущая тема + переключатель. */
export function useTheme(): { theme: Theme; toggle: () => void; setTheme: (t: Theme) => void } {
  const [theme, setThemeState] = useState<Theme>(read);

  useEffect(() => {
    apply(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // ignore
    }
  }, [theme]);

  const toggle = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  return { theme, toggle, setTheme: setThemeState };
}
